import { spawnSync } from 'node:child_process'
import { fileURLToPath } from 'node:url'
import { Command } from 'commander'
import { getCliVersion, PACKAGE_NAME } from '../version.js'
import { fetchLatestVersion } from '../update/registry.js'
import { runUpdateCheckRefresh } from '../update/notifier.js'
import { isNewerVersion } from '../update/semver.js'
import { writeUpdateState } from '../update/state.js'

type PackageManager = 'npm' | 'pnpm' | 'yarn' | 'bun'

interface InstallCommand {
  command: string
  args: string[]
}

export function registerUpdate(program: Command): void {
  program
    .command('update')
    .description('Update the Verentis CLI to the latest published version')
    .option('--check', 'report whether a newer version is available without installing')
    .option('--to <version>', 'install a specific version instead of the latest')
    .action(async (options: { check?: boolean; to?: string }) => {
      const current = getCliVersion()
      const target = await resolveTarget(options.to)

      if (options.check) {
        reportCheck(current, target, Boolean(options.to))
        return
      }

      if (!options.to && !isNewerVersion(target, current)) {
        console.log(`Already on the latest version (${current}).`)
        return
      }

      const install = resolveInstallCommand(`${PACKAGE_NAME}@${target}`)
      if (!install) {
        throw new Error(
          `Automatic update is only supported for a global install of ${PACKAGE_NAME}.\n` +
            `Update manually, for example:\n  npm install -g ${PACKAGE_NAME}@${target}`,
        )
      }

      console.log(`Updating ${PACKAGE_NAME}: ${current} → ${target}`)
      console.log(`$ ${install.command} ${install.args.join(' ')}`)
      const result = spawnSync(install.command, install.args, { stdio: 'inherit' })
      if (result.error) {
        const code = (result.error as NodeJS.ErrnoException).code
        const hint = code === 'ENOENT' ? ` ('${install.command}' was not found on PATH).` : `: ${result.error.message}`
        throw new Error(`Could not run the update${hint}\nUpdate manually:\n  ${install.command} ${install.args.join(' ')}`)
      }
      if (typeof result.status === 'number' && result.status !== 0) {
        throw new Error(
          `Update failed (${install.command} exited with code ${result.status}).\n` +
            `You can retry manually:\n  ${install.command} ${install.args.join(' ')}`,
        )
      }
      console.log(`Updated to ${target}. Run \`verentis --version\` to confirm.`)
    })

  // Internal: refreshes the cached latest-version marker used by the passive update notice.
  program
    .command('__update-check', { hidden: true })
    .description('(internal) refresh the cached latest-version marker')
    .action(async () => {
      await runUpdateCheckRefresh()
    })
}

async function resolveTarget(requested?: string): Promise<string> {
  if (requested) return requested.replace(/^v/i, '')
  const latest = await fetchLatestVersion({ timeoutMs: 8000 })
  if (!latest) {
    throw new Error(
      'Could not reach the package registry to determine the latest version. ' +
        'Check your connection, or point VERENTIS_UPDATE_REGISTRY at a reachable registry.',
    )
  }
  // Keep the passive-notice cache in sync with what we just learned.
  await writeUpdateState({ lastCheckAt: new Date().toISOString(), latestVersion: latest }).catch(() => {})
  return latest
}

function reportCheck(current: string, target: string, pinned: boolean): void {
  if (pinned) {
    console.log(`Current version:   ${current}`)
    console.log(`Requested version: ${target}`)
    return
  }
  if (isNewerVersion(target, current)) {
    console.log(`A new version is available: ${current} → ${target}`)
    console.log('Run `verentis update` to install it.')
  } else {
    console.log(`You are on the latest version (${current}).`)
  }
}

function resolveInstallCommand(spec: string): InstallCommand | null {
  const location = packageLocation()
  // Only self-update real installed packages — never a source checkout or `npm link`ed dev tree.
  if (!location || !location.replace(/\\/g, '/').includes('/node_modules/')) return null

  switch (detectPackageManager(location)) {
    case 'pnpm':
      return { command: 'pnpm', args: ['add', '-g', spec] }
    case 'yarn':
      return { command: 'yarn', args: ['global', 'add', spec] }
    case 'bun':
      return { command: 'bun', args: ['add', '-g', spec] }
    default:
      return { command: 'npm', args: ['install', '-g', spec] }
  }
}

/** Path of this package's own code on disk, used to infer the installing package manager. */
function packageLocation(): string | undefined {
  try {
    return fileURLToPath(import.meta.url)
  } catch {
    return process.argv[1]
  }
}

function detectPackageManager(location: string): PackageManager {
  const path = location.replace(/\\/g, '/')
  if (/\/(\.pnpm|pnpm)\//.test(path)) return 'pnpm'
  if (/\/(\.yarn|yarn)\//.test(path)) return 'yarn'
  if (/\/\.bun\//.test(path)) return 'bun'
  return 'npm'
}
